import { Button } from '@design-system/button';
import { Container } from '@design-system/container';
import { Link } from '@design-system/link';
import { Section } from '@design-system/section';
import classNames from 'classnames';
import React, { forwardRef } from 'react';

import { useDropdown } from '../../hooks/useDropdown';
import { getLinkIcon } from '../../utils/getLinkIcon';
import { getLinkSize } from '../../utils/getLinkSize';
import { isActive } from '../../utils/isActive';
import { NavigationWithSplitterItemProps } from './models';

export const NavigationWithSplitterItem = forwardRef<
  HTMLElement,
  NavigationWithSplitterItemProps
>(
  (
    {
      className,
      linkNavElement,
      activeUrlPath,
      href = '',
      ariaLabel,
      isMobileView,
      label,
      submenu,
      openIcon,
      closeIcon,
      expandTitle,
      onClickNavItem,
      handleMobileNavigationItemClick,
    },
    ref
  ) => {
    const LinkElement = linkNavElement || Link;
    const {
      handleDesktopBlur,
      handleDesktopOutsideBlur,
      handleMenuItemClick,
      handleKeyPress,
      isOpen,
      handleMouseOver,
    } = useDropdown();
    const onMouseOverDesktop =
      submenu && !isMobileView ? handleMouseOver : undefined;
    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
      if (onClickNavItem) onClickNavItem(label);
      handleMenuItemClick(e, Boolean(submenu), handleMobileNavigationItemClick);
    };

    return (
      <li
        className={classNames('cds-global-header__navigation-item', className, {
          'cds-global-header__navigation-item--has-children': Boolean(submenu),
        })}
        onBlur={handleDesktopOutsideBlur}
        onMouseLeave={handleDesktopBlur}
      >
        {href ? (
          <>
            <LinkElement
              className={classNames('cds-global-header__navigation-link', {
                'has-submenu': Boolean(submenu),
                active: isActive(activeUrlPath, href, []),
              })}
              onClick={handleClick}
              aria-current={decodeURIComponent(activeUrlPath) === href}
              onMouseOver={onMouseOverDesktop}
              href={href}
              variant="nav"
              size={getLinkSize(isMobileView)}
              aria-label={ariaLabel}
            >
              {label}
            </LinkElement>
            {Boolean(submenu) && (
              <Button
                variant={isMobileView ? 'primary-icon' : 'nav'}
                onClick={handleClick}
                onKeyDown={handleKeyPress}
                onMouseOver={onMouseOverDesktop}
                size="small"
                className="cds-global-header__navigation-arrow"
                aria-expanded={isOpen}
                iconSuffix={getLinkIcon(openIcon, closeIcon, isOpen)}
              >
                {`${expandTitle} ${label}`}
              </Button>
            )}
          </>
        ) : (
          <Button
            className={classNames('cds-global-header__navigation-btn', {
              'has-submenu': Boolean(submenu),
            })}
            aria-current={decodeURIComponent(activeUrlPath) === href}
            onClick={handleClick}
            onMouseOver={onMouseOverDesktop}
            onKeyDown={handleKeyPress}
            variant="nav"
            size={getLinkSize(isMobileView)}
            aria-label={ariaLabel}
            iconSuffix={
              submenu ? getLinkIcon(openIcon, closeIcon, isOpen) : undefined
            }
          >
            {label}
          </Button>
        )}
        {Boolean(submenu) && (
          <Section
            ref={ref}
            className={classNames('cds-global-header__sub-navigation', {
              'cds-global-header__sub-navigation--open': isOpen,
            })}
          >
            <Container maxWidth="max-grid">{submenu}</Container>
          </Section>
        )}
      </li>
    );
  }
);
